import { For, Show, createMemo, type Component, type JSX } from 'solid-js';
import { Panel } from '~ui/panel';
import { Heading, Text } from '~ui/text';
import { Table, type Column } from '~ui/table';
import { formatCurrency, formatNumber, formatPercent, perMonth } from '~/lib/format';
import { price } from '~data';
import type { Loan } from '~/data/portfolios';
import { accounts, allocatedTotal, loanPositions, deductibleShare, loanDeductibleInterest, loanInterest, loans, unallocated, type LoanPosition } from '~/stores/portfolio-store';
import { focusLoan, focusedLoanId, unfocusLoan } from '~/stores/loans-view-store';
import { loanSeries } from '~/stores/loan-series';
import { ArrowLeftIcon } from '~ui/icons';
import AccountChart from '~/features/account-chart';
import { LoanExposure } from '~/features/account-exposure';
import LoanFacts from '~/features/loan-facts';

// A loan as a card, matching the Accounts and Sandbox grids.
//
// Balance and rate are the only things typed in. Everything else is summed from
// the buys that named this loan — where the money went is recorded once, on the
// trade that spent it, so nothing here can disagree with the blotter.

/** One labelled figure. A row of bare numbers is a row of guesses. */
const Stat: Component<{ label: string; children: JSX.Element }> = (props) => (
    <span class='flex items-baseline gap-1 whitespace-nowrap'>
        <Text tone='muted'>{props.label}</Text>
        {props.children}
    </span>
);

/**
 * Whether this loan is in use.
 *
 * A balance, or something bought with it. The second half is not redundant: the
 * balance is a number you type and the purchases are recorded by the trades
 * themselves, so a loan that has funded $57,000 of buys against an untouched
 * balance box is emphatically in use — and testing the box alone hid it in the
 * collapsed slot, which renders no holdings and cannot be opened.
 */
const isDrawn = (loan: Loan): boolean => loan.balance !== 0 || allocatedTotal(loan) > 0;

const LoanCard: Component<{ loan: Loan }> = (props) => {

    return (
        <div class='flex min-h-56 min-w-0 flex-col bg-background'>
            {/* px-4 py-3, the same box the stat cards and the account cards
                use. Three card shapes on one app with different gutters read as
                three systems. */}
            <div class='flex shrink-0 items-start justify-between gap-3 py-3 pl-4'>
                <div class='flex min-w-0 flex-col items-start gap-1'>
                    {/* The heading is the way in, same as an account card. */}
                    <button
                        type='button'
                        class='block min-w-0 cursor-default rounded text-left hover:bg-muted'
                        title={`Open ${props.loan.label}`}
                        onClick={() => focusLoan(props.loan.id)}>
                        <Heading level={1} class='block min-w-0 truncate text-2xl tracking-tight'>
                            {props.loan.label}
                        </Heading>
                    </button>
                    <div class='flex flex-wrap items-baseline gap-x-3 gap-y-1'>
                        <Stat label='Interest'>
                            <Text tone='red'>{formatCurrency(loanInterest(props.loan))}</Text>
                        </Stat>
                        {/* Which share qualifies is not a choice — it falls out
                            of the account the borrowed money was invested in. */}
                        <Stat label='Deductible'>
                            <Text tone={deductibleShare(props.loan) > 0 ? 'green' : undefined}>
                                {formatCurrency(loanDeductibleInterest(props.loan))} · {formatPercent(deductibleShare(props.loan) * 100, 0)}
                            </Text>
                        </Stat>
                        {/* Negative means more has been allocated than borrowed.
                            Shown rather than clamped: it is a data error worth
                            seeing. */}
                        <Stat label='Uninvested'>
                            <Text tone={unallocated(props.loan) < 0 ? 'red' : undefined}>{formatCurrency(unallocated(props.loan))}</Text>
                        </Stat>
                    </div>
                </div>

                {/* Read-only. Balance, rate, limit and lender are defined once,
                    in Profile, because a facility is a fact about you rather
                    than about the book on screen — two boxes here and two there
                    would be two places to keep one rate current. */}
                <div class='flex shrink-0 flex-col items-end gap-1 whitespace-nowrap pr-4'>
                    <Text weight='medium' class='text-xl tracking-tight'>
                        {formatCurrency(props.loan.balance)}
                    </Text>
                    <Text tone='muted'>
                        {formatPercent(props.loan.rate, 2)}
                        {props.loan.issuer ? ` · ${props.loan.issuer}` : ''}
                    </Text>
                </div>
            </div>

            {/* What it bought. Accounts were what this said before, which is
                where the money went rather than what it went into — and the
                fund is the thing you are actually deciding about. */}
            <div class='min-h-0 flex-1 overflow-y-auto scrollbar-hide'>
                <Show
                    when={loanPositions(props.loan).length > 0}
                    fallback={
                        <Text tone='muted' class='block px-4 py-3'>
                            Nothing held on this
                        </Text>
                    }>
                    <For each={loanPositions(props.loan)}>
                        {(position) => (
                            <div class='flex h-row items-center justify-between gap-2 border-b px-4'>
                                <span class='flex min-w-0 items-baseline gap-2'>
                                    <Text weight='medium' class='shrink-0'>
                                        {position.symbol}
                                    </Text>
                                    <Text tone='muted' class='truncate'>
                                        {accounts().find((a) => a.id === position.accountId)?.label ?? ''}
                                    </Text>
                                </span>
                                <span class='flex shrink-0 items-baseline gap-3'>
                                    <Text tone='muted'>{formatNumber(position.units, { maximumFractionDigits: 4 })} u</Text>
                                    <Text weight='medium'>{formatCurrency(position.cost)}</Text>
                                </span>
                            </div>
                        )}
                    </For>
                </Show>
            </div>
        </div>
    );
};

// What the loan is still in, given the room the card cannot spare.
const POSITION_COLUMNS: Column<LoanPosition>[] = [
    { id: 'symbol', header: 'Symbol', size: 0.8, cell: (p) => <Text weight='medium'>{p().symbol}</Text> },
    { id: 'account', header: 'Account', size: 1, cell: (p) => <Text class='truncate'>{accounts().find((a) => a.id === p().accountId)?.label ?? ''}</Text> },
    // Fractional, and correctly so: the loan's share of a position is the share
    // of its cost base the loan paid for, and the units follow that fraction.
    { id: 'units', header: 'Units', align: 'right', size: 0.9, cell: (p) => formatNumber(p().units, { maximumFractionDigits: 4 }) },
    { id: 'price', header: 'Price', align: 'right', size: 0.7, cell: (p) => formatCurrency(price(p().symbol)) },
    { id: 'cost', header: 'Cost', align: 'right', size: 0.9, cell: (p) => formatCurrency(p().cost) },
    {
        id: 'value',
        header: 'Value',
        align: 'right',
        size: 0.9,
        cell: (p) => <Text weight='medium'>{formatCurrency(price(p().symbol) == null ? null : p().units * price(p().symbol)!)}</Text>,
    },
    {
        id: 'gain',
        header: 'Gain',
        align: 'right',
        size: 0.9,
        cell: (p) => {
            const unit = price(p().symbol);
            const gain = unit == null ? null : p().units * unit - p().cost;
            return <Text tone={gain == null ? undefined : gain >= 0 ? 'green' : 'red'}>{formatCurrency(gain)}</Text>;
        },
    },
];

/**
 * One loan with the page to itself.
 *
 * Laid out exactly like a focused account, because it is the same question asked
 * of a different pile of money: a stat column on the left, the history and what
 * it holds in the middle, what that resolves to underneath, and the blotter
 * beside it. A loan that looked nothing like an account made you re-learn the
 * screen for the sake of the word at the top of it.
 */
const FocusedLoan: Component<{ loan: Loan }> = (props) => {
    const positions = createMemo(() => loanPositions(props.loan));

    return (
        <>
            {/* The arrow is part of the title rather than a bar above it — same
                as the account header, and for the same reason: a whole row spent
                on one button, over a heading that is already the thing you would
                click to leave. */}
            <div class='flex shrink-0 items-center justify-between gap-3 border-b py-2 pl-3'>
                <button type='button' class='group flex min-w-0 cursor-default items-center gap-1 rounded pr-2' title='Back to every loan' onClick={unfocusLoan}>
                    <span class='shrink-0 text-foreground transition-transform duration-150 group-hover:-translate-x-1 [&_svg]:size-5'>
                        <ArrowLeftIcon />
                    </span>
                    <Heading level={1} class='block min-w-0 truncate text-3xl tracking-tight'>
                        {props.loan.label}
                    </Heading>
                </button>
                <div class='flex shrink-0 flex-col items-end whitespace-nowrap pr-3'>
                    <Text weight='medium' class='text-xl tracking-tight'>
                        {formatCurrency(props.loan.balance)}
                    </Text>
                    <Text tone='red'>{perMonth(loanInterest(props.loan))}</Text>
                </div>
            </div>

            <div class='flex min-h-0 flex-1'>
                {/* The facts column, same shape and width the Accounts and Fund
                    pages use. Left, because it is the fixed reference the things
                    beside it are read against. */}
                <div class='min-h-0 w-1/5 shrink-0 overflow-y-auto border-r scrollbar-hide'>
                    <LoanFacts loan={props.loan} />
                </div>

                <div class='flex min-w-0 flex-1 flex-col'>
                    {/* What the borrowed money is worth against what it put in.
                        The gap is whether borrowing was worth doing, which is
                        the page's whole question and the one no single figure
                        on it answers over time. */}
                    <div class='shrink-0 border-b'>
                        <AccountChart points={loanSeries(props.loan.id)} costLabel='Borrowed' height='h-56' />
                    </div>

                    {/* Funds, with the account each sits in beside it — not a
                        list of accounts, which read as though the account were
                        the purchase. */}
                    <div class='min-h-0 flex-1'>
                        <Show
                            when={positions().length > 0}
                            fallback={
                                <Text tone='muted' class='block px-3 py-3'>
                                    Nothing held on this
                                </Text>
                            }>
                            <Table columns={POSITION_COLUMNS} rows={positions()} />
                        </Show>
                    </div>

                    {/* Borrow against the house to buy two covered-call funds
                        and you may have bought the same five banks twice with
                        money you owe. Scoped to the borrowed share, so the
                        shares are of the borrowing rather than of the book. */}
                    <Show when={positions().length > 0}>
                        <div class='flex h-56 shrink-0 flex-col border-t'>
                            <LoanExposure loanId={props.loan.id} />
                        </div>
                    </Show>
                </div>
            </div>
        </>
    );
};

/** Cells the grid actually renders: the drawn facilities, plus the one slot the
    undrawn ones share. */
const cardCount = (): number => loans().filter(isDrawn).length + (loans().some((l) => !isDrawn(l)) ? 1 : 0);

const Loans: Component = () => (
    <Panel>
        <Show when={loans().find((l) => l.id === focusedLoanId())} keyed>
            {(loan) => <FocusedLoan loan={loan} />}
        </Show>
        <Show when={focusedLoanId() == null}>
        <div class='min-h-0 flex-1 overflow-y-auto scrollbar-hide'>
            <Show
                when={loans().length > 0}
                fallback={
                    <div class='flex h-16 items-center justify-center'>
                        <Text tone='muted'>No loans</Text>
                    </div>
                }>
                <div class='grid grid-cols-2 gap-px border-b bg-border'>
                    <For each={loans().filter(isDrawn)}>{(loan) => <LoanCard loan={loan} />}</For>
                    {/* Filler for the odd cell — see the note on the Accounts
                        grid. The seam colour showing through a missing cell
                        reads as a grey panel, not as a gap. */}
                    <Show when={cardCount() % 2 === 1}>
                        <div class='bg-background' />
                    </Show>

                    {/* Undrawn facilities share a slot rather than taking a card
                        each, same as the unused accounts. A line you have not
                        drawn on is capacity, not a position, and it needs one
                        row rather than a card of zeroes. */}
                    <Show when={loans().some((l) => !isDrawn(l))}>
                        <div class='flex min-h-56 min-w-0 flex-col items-start gap-1 bg-background px-4 py-3'>
                            <Text tone='muted'>Nothing drawn</Text>
                            <For each={loans().filter((l) => !isDrawn(l))}>
                                {(loan) => (
                                    <div class='flex items-baseline gap-2 pt-1'>
                                        <Text weight='medium'>{loan.label}</Text>
                                        <Text tone='muted'>
                                            {formatPercent(loan.rate, 2)}
                                            {loan.limit == null ? '' : ` · ${formatCurrency(loan.limit)} available`}
                                        </Text>
                                    </div>
                                )}
                            </For>
                        </div>
                    </Show>
                </div>
            </Show>
        </div>
        </Show>
    </Panel>
);

export default Loans;
